import { Link } from "react-router";

export type Tone = "primary" | "amber" | "red" | "green" | "muted";

const toneClasses: Record<Tone, { bg: string; icon: string }> = {
  primary: { bg: "bg-primary/10", icon: "text-primary" },
  amber: { bg: "bg-amber-500/10", icon: "text-amber-600" },
  red: { bg: "bg-red-500/10", icon: "text-red-600" },
  green: { bg: "bg-green-500/10", icon: "text-green-600" },
  muted: { bg: "bg-muted", icon: "text-muted-foreground" },
};

interface IntentCardHeaderProps {
  /**
   * Typically a string, but may be a small flex row of `<Icon /> Text`
   * to match the homepage metric-card pattern.
   */
  label: React.ReactNode;
  hero: React.ReactNode;
  /**
   * Optional small badge rendered directly under the hero, above the
   * subtitle. Used on the three pyramid cards to carry a qualitative
   * tier tag for the hero number.
   */
  badge?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon: React.ReactNode;
  tone: Tone;
}

/**
 * Shared header used by Pulse and the three intent cards. Hero line is
 * the card's main KPI; badge and subtitle are supporting. The icon
 * badge sits on the right with a tone-matched tint.
 */
export function IntentCardHeader({
  label,
  hero,
  badge,
  subtitle,
  icon,
  tone,
}: IntentCardHeaderProps) {
  const tones = toneClasses[tone];
  return (
    <div>
      {/* Top row: label on the left, icon on the right. Hero gets its
          own full-width row below so long labels ("strategic & key
          contacts", "orders last 30d") don't wrap around the icon. */}
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className={`p-2 rounded-full shrink-0 ${tones.bg}`}>
          <span className={`block ${tones.icon}`}>{icon}</span>
        </div>
      </div>
      <div className="text-2xl lg:text-3xl font-bold text-foreground leading-tight break-words">
        {hero}
      </div>
      {badge && <div className="mt-1.5 mb-3">{badge}</div>}
      {subtitle && (
        <div className="text-sm text-muted-foreground mt-1">{subtitle}</div>
      )}
    </div>
  );
}

export interface CardFactor {
  label: string;
  value: React.ReactNode;
  /** Longer explanation shown only in the dialog view. */
  narrative?: string;
  /** Weight of this factor in the weighted hero score, when applicable. */
  weight?: number;
  /** Contribution to the weighted hero score (score × weight ÷ sum of weights). */
  contribution?: number;
  /** Small domain icon rendered next to the label in the dialog view. */
  icon?: React.ReactNode;
}

/**
 * Compact label/value list rendered on the card body, below the hero.
 * Each of the three intent cards surfaces its 4-6 supporting factors
 * in the same shape — the units differ (£, counts, scores) but the
 * row pattern is shared.
 */
export function FactorList({ factors }: { factors: CardFactor[] }) {
  return (
    <div className="space-y-1.5 text-sm">
      {factors.map((f) => (
        <div
          key={f.label}
          className="flex justify-between items-baseline gap-3"
        >
          <span className="text-muted-foreground truncate">{f.label}</span>
          <span className="font-medium text-foreground tabular-nums whitespace-nowrap">
            {f.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Expanded factor view used inside dialogs. Shows the narrative and,
 * when present, the weight/contribution breakdown driving a weighted
 * hero score (Account health).
 */
export function FactorDetailList({
  factors,
  showWeights = false,
}: {
  factors: CardFactor[];
  showWeights?: boolean;
}) {
  return (
    <div className="space-y-4 text-sm">
      {factors.map((f) => (
        <div key={f.label}>
          <div className="flex items-baseline gap-2 flex-wrap">
            {f.icon && (
              <span className="text-muted-foreground shrink-0 self-center [&_svg]:size-4">
                {f.icon}
              </span>
            )}
            <span className="font-medium">{f.label}</span>
            {f.value === "—" || f.value == null || f.value === "" ? (
              <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
                —
              </span>
            ) : (
              <span className="text-sm font-semibold text-primary tabular-nums whitespace-nowrap bg-primary/10 rounded-md px-2 py-0.5">
                {f.value}
              </span>
            )}
            {showWeights && f.weight != null && f.contribution != null && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                weight {f.weight} · contrib {f.contribution.toFixed(1)}
              </span>
            )}
          </div>
          {f.narrative && (
            <div className="text-muted-foreground mt-1 leading-relaxed">
              {f.narrative}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

interface ClickableStatProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  /**
   * Where the cell navigates. Omit to render the same figure as plain text —
   * used when the destination belongs to a feature that is not live here, so
   * the number stays informative without becoming a dead-end link.
   */
  to?: string;
  muted?: boolean;
  /** Centre-align the label/value/sub text instead of left-align. */
  centered?: boolean;
}

/**
 * Tier-2 stat rendered as a hover-card link. The entire cell gets a
 * background shift on hover so the label, value, and sub all signal
 * navigability without the value alone carrying the affordance.
 */
export function ClickableStat({
  label,
  value,
  sub,
  to,
  muted,
  centered,
}: ClickableStatProps) {
  const valueClass = `text-2xl font-semibold tabular-nums leading-none ${
    muted ? "text-muted-foreground" : "text-foreground"
  }`;
  const alignment = centered ? "text-center items-center" : "text-left";
  const body = (
    <div className={`flex flex-col gap-1 min-w-0 ${alignment}`}>
      <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">
        {label}
      </div>
      <div className={valueClass}>{value}</div>
      {sub && (
        <div className="text-xs text-muted-foreground truncate">{sub}</div>
      )}
    </div>
  );

  if (!to) {
    return <div className="block min-w-0 p-2 -m-2">{body}</div>;
  }

  return (
    <Link
      to={to}
      className="block min-w-0 rounded-md hover:bg-muted/50 transition-colors p-2 -m-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {body}
    </Link>
  );
}
